import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Form, InputGroup, Modal, Spinner, Tab, Table, Tabs } from "react-bootstrap";
import { toast } from "react-toastify";
import apiCall from "./../../API/API";
import "./PlanSettings.css";

const FREQUENCIES = ["Monthly", "Daily", "Yearly"];
const CATEGORY_LABELS = { fix: "Fix Plan", real_estate: "Real Estate" };

const inr = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

/** Growth over a whole period, in % of the amount, for a rate paid at the given frequency. */
const totalGrowth = (rate, frequency, months) => {
  const r = Number(rate) || 0;
  const m = Number(months) || 0;
  if (frequency === "Daily") return (r * m * 365) / 12;
  if (frequency === "Yearly") return (r * m) / 12;
  return r * m;
};

const monthlyRate = (rate, frequency) => {
  const r = Number(rate) || 0;
  if (frequency === "Daily") return (r * 365) / 12;
  if (frequency === "Yearly") return r / 12;
  return r;
};

const round = (n, d = 2) => Math.round(n * 10 ** d) / 10 ** d;

const EMPTY_PACKAGE = {
  packageId: null,
  name: "",
  category: "fix",
  package_type: "investor",
  color: "#16A34A",
  min_amount: "",
  max_amount: "",
  multiplier: 1,
  roi_percent: "",
  roi_frequency: "Monthly",
  period_months: "",
  total_capping: 200,
  status: 1,
};

/* ------------------------------------------------------------------ */
/* Packages                                                            */
/* ------------------------------------------------------------------ */

const PackageModal = ({ show, initial, packages, onClose, onSave, saving }) => {
  const [form, setForm] = useState(EMPTY_PACKAGE);
  const isNew = !initial?.packageId;

  useEffect(() => {
    if (show) setForm(initial ? { ...EMPTY_PACKAGE, ...initial } : EMPTY_PACKAGE);
  }, [show, initial]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const growth = totalGrowth(form.roi_percent, form.roi_frequency, form.period_months);
  const perMonth = monthlyRate(form.roi_percent, form.roi_frequency);
  const cappedAt = perMonth > 0 ? Number(form.total_capping) / perMonth : 0;
  const paysOut = Math.min(growth, Number(form.total_capping) || 0);

  const overlaps = packages.filter(
    (p) =>
      p.packageId !== form.packageId &&
      p.status === 1 &&
      Number(form.min_amount) <= p.max_amount &&
      Number(form.max_amount) >= p.min_amount
  );

  const submit = (e) => {
    e.preventDefault();
    onSave({
      packageId: form.packageId ?? undefined,
      name: form.name.trim(),
      category: form.category,
      package_type: form.package_type || "investor",
      color: form.color,
      min_amount: Number(form.min_amount),
      max_amount: Number(form.max_amount),
      multiplier: Number(form.multiplier),
      roi_percent: Number(form.roi_percent),
      roi_frequency: form.roi_frequency,
      period_months: Number(form.period_months),
      total_capping: Number(form.total_capping),
      status: Number(form.status),
    });
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered className="plan-settings-modal">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>{isNew ? "Add Package" : `Edit Package #${form.packageId}`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ps-form-grid">
            <Form.Group>
              <Form.Label>Package name</Form.Label>
              <Form.Control value={form.name} onChange={set("name")} placeholder="e.g. Associate" required maxLength={80} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Plan</Form.Label>
              <Form.Select value={form.category} onChange={set("category")}>
                {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Form.Select>
              <Form.Text>Users see packages grouped by plan.</Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>Package type</Form.Label>
              <Form.Select value={form.package_type || "investor"} onChange={set("package_type")}>
                <option value="advisor">Advisor</option>
                <option value="investor">Investor</option>
              </Form.Select>
              <Form.Text>A label for members. Every active ID gets ranks and rank incomes, whatever the package type.</Form.Text>
            </Form.Group>
            <Form.Group className="ps-span-2">
              <Form.Label>Status colour</Form.Label>
              <div className="ps-color-row">
                <Form.Control
                  type="color"
                  value={form.color || "#16A34A"}
                  onChange={set("color")}
                  className="ps-color-input"
                  title="Colour of members on this package"
                />
                <span className="ps-color-sample" style={{ color: form.color, background: `${form.color}1A`, borderColor: `${form.color}55` }}>
                  {form.name || "Package"}
                </span>
              </div>
              <Form.Text>Members on this package show this colour in member lists and the genealogy tree.</Form.Text>
            </Form.Group>

            <Form.Group>
              <Form.Label>Minimum amount</Form.Label>
              <InputGroup>
                <InputGroup.Text>₹</InputGroup.Text>
                <Form.Control type="number" min="1" step="any" value={form.min_amount} onChange={set("min_amount")} required />
              </InputGroup>
              <Form.Text>{form.min_amount ? inr(form.min_amount) : " "}</Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>Maximum amount</Form.Label>
              <InputGroup>
                <InputGroup.Text>₹</InputGroup.Text>
                <Form.Control type="number" min="1" step="any" value={form.max_amount} onChange={set("max_amount")} required />
              </InputGroup>
              <Form.Text>{form.max_amount ? inr(form.max_amount) : " "}</Form.Text>
            </Form.Group>

            <Form.Group>
              <Form.Label>Growth rate</Form.Label>
              <InputGroup>
                <Form.Control type="number" min="0" max="100" step="any" value={form.roi_percent} onChange={set("roi_percent")} required />
                <InputGroup.Text>%</InputGroup.Text>
                <Form.Select value={form.roi_frequency} onChange={set("roi_frequency")} className="ps-frequency">
                  {FREQUENCIES.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </Form.Select>
              </InputGroup>
            </Form.Group>
            <Form.Group>
              <Form.Label>Period</Form.Label>
              <InputGroup>
                <Form.Control type="number" min="1" max="240" step="any" value={form.period_months} onChange={set("period_months")} required />
                <InputGroup.Text>months</InputGroup.Text>
              </InputGroup>
            </Form.Group>

            <Form.Group>
              <Form.Label>Capping (total payout limit)</Form.Label>
              <InputGroup>
                <Form.Control type="number" min="1" max="1000" step="any" value={form.total_capping} onChange={set("total_capping")} required />
                <InputGroup.Text>% of amount</InputGroup.Text>
              </InputGroup>
              <Form.Text>200% = 2X</Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>Amount multiple</Form.Label>
              <InputGroup>
                <InputGroup.Text>₹</InputGroup.Text>
                <Form.Control type="number" min="1" step="any" value={form.multiplier} onChange={set("multiplier")} required />
              </InputGroup>
              <Form.Text>Amounts above the minimum must be a multiple of this.</Form.Text>
            </Form.Group>

            <Form.Group className="ps-span-2">
              <Form.Check
                type="switch"
                id="package-status"
                label={Number(form.status) === 1 ? "Active: users can buy this package" : "Disabled: hidden from new purchases"}
                checked={Number(form.status) === 1}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.checked ? 1 : 0 }))}
              />
            </Form.Group>
          </div>

          {Number(form.roi_percent) > 0 && Number(form.period_months) > 0 && (
            <div className="ps-summary">
              <div>
                <span className="ps-summary-label">Over the period</span>
                <strong>{round(growth)}%</strong>
              </div>
              <div>
                <span className="ps-summary-label">Paid out (after capping)</span>
                <strong>{round(paysOut)}%</strong>
              </div>
              <div>
                <span className="ps-summary-label">Capping reached in</span>
                <strong>{cappedAt ? `${round(cappedAt, 1)} months` : "—"}</strong>
              </div>
              {Number(form.min_amount) > 0 && (
                <div>
                  <span className="ps-summary-label">On {inr(form.min_amount)}</span>
                  <strong>{inr((Number(form.min_amount) * paysOut) / 100)}</strong>
                </div>
              )}
            </div>
          )}

          {overlaps.length > 0 && (
            <div className="ps-note ps-note-info">
              Amount range overlaps {overlaps.map((p) => `"${p.name}"`).join(", ")}. That is allowed: users pick the
              package by name when buying.
            </div>
          )}
          {!isNew && (
            <div className="ps-note ps-note-warn">
              Growth rate and capping apply to running orders of this package from the next payout. The period applies
              to new orders only.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? <Spinner size="sm" /> : isNew ? "Add Package" : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const PackagesTab = ({ planId, packages, onSaved }) => {
  const [editing, setEditing] = useState(null);
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(null);

  const save = async (payload) => {
    setSaving(true);
    const { data, error } = await apiCall("POST", "/plan-settings/save-package", { planId, ...payload });
    setSaving(false);
    if (error) return toast.error(error.message || "Could not save the package.");
    toast.success(data?.message || "Package saved.");
    setShow(false);
    onSaved();
  };

  const toggle = async (p) => {
    setToggling(p.packageId);
    const { data, error } = await apiCall("POST", "/plan-settings/save-package", {
      planId,
      packageId: p.packageId,
      name: p.name,
      category: p.category,
      package_type: p.package_type,
      color: p.color,
      min_amount: p.min_amount,
      max_amount: p.max_amount,
      multiplier: p.multiplier,
      roi_percent: p.roi_percent,
      roi_frequency: p.roi_frequency,
      period_months: p.period_months,
      total_capping: p.total_capping,
      status: p.status === 1 ? 0 : 1,
    });
    setToggling(null);
    if (error) return toast.error(error.message || "Could not change the status.");
    toast.success(data?.message || "Status changed.");
    onSaved();
  };

  return (
    <div className="ps-panel">
      <div className="ps-panel-head">
        <div>
          <h3>Packages</h3>
          <p>What users can buy. Disable a package to stop new purchases; running orders are not affected.</p>
        </div>
        <Button onClick={() => { setEditing(null); setShow(true); }}>+ Add Package</Button>
      </div>

      <div className="ps-table-wrap">
        <Table hover className="ps-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Package</th>
              <th>Amount range</th>
              <th>Growth</th>
              <th>Period</th>
              <th>Capping</th>
              <th>Status</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {packages.length === 0 && (
              <tr><td colSpan={8} className="ps-empty">No packages yet. Add the first one.</td></tr>
            )}
            {packages.map((p) => (
              <tr key={p.packageId} className={p.status === 1 ? "" : "ps-row-muted"}>
                <td>{p.packageId}</td>
                <td>
                  <div className="ps-strong">
                    <span className="ps-color-dot" style={{ background: p.color }} title="Status colour" />
                    {p.name}
                  </div>
                  <div className="ps-muted ps-small">
                    {CATEGORY_LABELS[p.category] || "Fix Plan"} ·{" "}
                    <span className={p.package_type === "advisor" ? "ps-type-advisor" : ""}>
                      {p.package_type === "advisor" ? "Advisor" : "Investor"}
                    </span>
                  </div>
                </td>
                <td>{inr(p.min_amount)} – {inr(p.max_amount)}</td>
                <td>{p.roi_percent}% <span className="ps-muted">{p.roi_frequency.toLowerCase()}</span></td>
                <td>{p.period_months} <span className="ps-muted">months</span></td>
                <td>{p.total_capping}% <span className="ps-muted">({round(p.total_capping / 100, 2)}X)</span></td>
                <td>
                  <Badge bg={p.status === 1 ? "success" : "secondary"} className="ps-badge">
                    {p.status === 1 ? "Active" : "Disabled"}
                  </Badge>
                </td>
                <td className="text-end ps-actions">
                  <Button size="sm" variant="outline-primary" onClick={() => { setEditing(p); setShow(true); }}>Edit</Button>
                  <Button
                    size="sm"
                    variant={p.status === 1 ? "outline-danger" : "outline-success"}
                    onClick={() => toggle(p)}
                    disabled={toggling === p.packageId}
                  >
                    {toggling === p.packageId ? <Spinner size="sm" /> : p.status === 1 ? "Disable" : "Enable"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <PackageModal
        show={show}
        initial={editing}
        packages={packages}
        saving={saving}
        onClose={() => setShow(false)}
        onSave={save}
      />
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Level income tables                                                 */
/* ------------------------------------------------------------------ */

// Level Income tabs are hidden from Plan Settings for now; kept to bring them back easily.
// eslint-disable-next-line no-unused-vars
const LevelTable = ({ planId, type, title, description, table, onSaved }) => {
  const [draft, setDraft] = useState(table);
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(table), [table]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(table), [draft, table]);
  const isFix = draft.income_type === "fix";

  const setLevel = (i, key, value) =>
    setDraft((d) => ({
      ...d,
      levels: d.levels.map((l, idx) => (idx === i ? { ...l, [key]: value } : l)),
    }));

  const addLevel = () =>
    setDraft((d) => {
      const last = d.levels[d.levels.length - 1];
      return {
        ...d,
        levels: [
          ...d.levels,
          { level: d.levels.length + 1, income: last?.income ?? 0, direct_required: 0, business_required: 0, total_team_required: 0 },
        ],
      };
    });

  const removeLevel = (i) =>
    setDraft((d) => ({ ...d, levels: d.levels.filter((_, idx) => idx !== i).map((l, idx) => ({ ...l, level: idx + 1 })) }));

  const total = draft.levels.reduce((s, l) => s + (Number(l.income) || 0), 0);

  const save = async () => {
    setSaving(true);
    const { data, error } = await apiCall("POST", "/plan-settings/save-levels", {
      planId,
      type,
      income_type: draft.income_type,
      status: Number(draft.status),
      levels: draft.levels.map((l) => ({
        income: Number(l.income) || 0,
        direct_required: Number(l.direct_required) || 0,
        business_required: Number(l.business_required) || 0,
        total_team_required: Number(l.total_team_required) || 0,
      })),
    });
    setSaving(false);
    if (error) return toast.error(error.message || "Could not save.");
    toast.success(data?.message || "Saved.");
    onSaved();
  };

  return (
    <div className="ps-panel">
      <div className="ps-panel-head">
        <div>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
        <div className="ps-head-controls">
          <Form.Check
            type="switch"
            id={`${type}-status`}
            label={Number(draft.status) === 1 ? "Enabled" : "Disabled"}
            checked={Number(draft.status) === 1}
            onChange={(e) => setDraft((d) => ({ ...d, status: e.target.checked ? 1 : 0 }))}
          />
          <Form.Select
            value={draft.income_type}
            onChange={(e) => setDraft((d) => ({ ...d, income_type: e.target.value }))}
            className="ps-type-select"
          >
            <option value="fix">Fixed amount (₹)</option>
            <option value="percentage">Percentage (%)</option>
          </Form.Select>
        </div>
      </div>

      <div className="ps-table-wrap">
        <Table className="ps-table ps-table-edit">
          <thead>
            <tr>
              <th>Level</th>
              <th>Income {isFix ? "(₹)" : "(%)"}</th>
              <th>Directs required</th>
              <th>Business required (₹)</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {draft.levels.length === 0 && (
              <tr><td colSpan={5} className="ps-empty">No levels. Add one to start.</td></tr>
            )}
            {draft.levels.map((l, i) => (
              <tr key={i}>
                <td className="ps-strong">Level {i + 1}</td>
                <td>
                  <Form.Control type="number" min="0" step="any" size="sm" value={l.income} onChange={(e) => setLevel(i, "income", e.target.value)} />
                </td>
                <td>
                  <Form.Control type="number" min="0" step="1" size="sm" value={l.direct_required} onChange={(e) => setLevel(i, "direct_required", e.target.value)} />
                </td>
                <td>
                  <Form.Control type="number" min="0" step="any" size="sm" value={l.business_required} onChange={(e) => setLevel(i, "business_required", e.target.value)} />
                </td>
                <td className="text-end">
                  <Button size="sm" variant="link" className="ps-remove" onClick={() => removeLevel(i)} title="Remove level">✕</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="ps-footer">
        <div className="ps-footer-left">
          <Button size="sm" variant="outline-primary" onClick={addLevel} disabled={draft.levels.length >= 100}>+ Add Level</Button>
          <span className="ps-muted">
            {draft.levels.length} levels · total {isFix ? inr(total) : `${round(total)}%`}
          </span>
        </div>
        <div className="ps-footer-right">
          {dirty && <span className="ps-unsaved">Unsaved changes</span>}
          <Button variant="outline-secondary" size="sm" onClick={() => setDraft(table)} disabled={!dirty || saving}>Reset</Button>
          <Button size="sm" onClick={save} disabled={!dirty || saving}>
            {saving ? <Spinner size="sm" /> : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Ranks                                                               */
/* ------------------------------------------------------------------ */

const RankModal = ({ show, rank, onClose, onSave, saving }) => {
  const [form, setForm] = useState(rank || {});
  useEffect(() => { if (show) setForm(rank || {}); }, [show, rank]);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    onSave({
      rankId: form.rankId,
      rank_name: String(form.rank_name || "").trim(),
      required_business: Number(form.required_business),
      reward_amount: Number(form.reward_amount),
      reward_description: String(form.reward_description || "").trim(),
      reward_status: Number(form.reward_status),
      strong_leg_ratio: Number(form.strong_leg_ratio),
      second_strong_leg_ratio: 0, // the plan uses only top leg + other legs
      other_legs_ratio: Number(form.other_legs_ratio),
    });
  };

  const ratioTotal = (Number(form.strong_leg_ratio) || 0) + (Number(form.other_legs_ratio) || 0);
  const ratioOk = Math.abs(ratioTotal - 100) < 0.001;

  return (
    <Modal show={show} onHide={onClose} centered className="plan-settings-modal">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Reward {form.rankId}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ps-form-grid">
            <Form.Group className="ps-span-2">
              <Form.Label>Reward name</Form.Label>
              <Form.Control value={form.rank_name || ""} onChange={set("rank_name")} required maxLength={60} />
            </Form.Group>
            <Form.Group className="ps-span-2">
              <Form.Label>Required business</Form.Label>
              <Form.Control type="number" min="0" step="any" value={form.required_business ?? ""} onChange={set("required_business")} required />
              <Form.Text>Team business needed to reach this reward (₹).</Form.Text>
            </Form.Group>
            <div className="ps-span-2">
              <Form.Label>Business split by leg</Form.Label>
              <div className="ps-ratio-grid">
                {[
                  ["strong_leg_ratio", "Top leg"],
                  ["other_legs_ratio", "Other legs"],
                ].map(([key, label]) => (
                  <InputGroup key={key} size="sm">
                    <InputGroup.Text>{label}</InputGroup.Text>
                    <Form.Control type="number" min="0" max="100" step="any" value={form[key] ?? ""} onChange={set(key)} required />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                ))}
              </div>
              <Form.Text className={ratioOk ? "" : "ps-error-text"}>
                Total {Math.round(ratioTotal * 100) / 100}% {ratioOk ? "" : "(must be 100%)"}
                {Number(form.required_business) > 0 && ratioOk && (
                  <> · needs {[
                    ["top leg", form.strong_leg_ratio],
                    ["other legs", form.other_legs_ratio],
                  ]
                    .filter(([, r]) => Number(r) > 0)
                    .map(([name, r]) => `${Math.round((Number(form.required_business) * Number(r)) / 100 * 100) / 100} from ${name}`)
                    .join(", ")}</>
                )}
              </Form.Text>
            </div>
            <Form.Group>
              <Form.Label>Reward value (cash)</Form.Label>
              <InputGroup>
                <InputGroup.Text>₹</InputGroup.Text>
                <Form.Control type="number" min="0" step="any" value={form.reward_amount ?? ""} onChange={set("reward_amount")} required />
              </InputGroup>
            </Form.Group>
            <Form.Group className="d-flex align-items-end">
              <Form.Check
                type="switch"
                id="rank-reward-status"
                label={Number(form.reward_status) === 1 ? "Reward active" : "Reward off"}
                checked={Number(form.reward_status) === 1}
                onChange={(e) => setForm((f) => ({ ...f, reward_status: e.target.checked ? 1 : 0 }))}
              />
            </Form.Group>
            <Form.Group className="ps-span-2">
              <Form.Label>Reward item</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                maxLength={200}
                value={form.reward_description || ""}
                onChange={set("reward_description")}
                placeholder="e.g. ₹31,000 + Goa Tour"
              />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" disabled={saving || !ratioOk}>{saving ? <Spinner size="sm" /> : "Save Reward"}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const RanksTab = ({ planId, ranks, onSaved }) => {
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async (payload) => {
    setSaving(true);
    const { data, error } = await apiCall("POST", "/plan-settings/save-rank", { planId, ...payload });
    setSaving(false);
    if (error) return toast.error(error.message || "Could not save the reward.");
    toast.success(data?.message || "Rank saved.");
    setEditing(null);
    onSaved();
  };

  return (
    <div className="ps-panel">
      <div className="ps-panel-head">
        <div>
          <h3>Rewards</h3>
          <p>The rewards members reach with their team business: the business needed for each, how it splits between the top leg and the other legs, and the reward (cash value and item). Achievers are settled on the Reward Achievers page.</p>
        </div>
      </div>
      <div className="ps-table-wrap">
        <Table hover className="ps-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Reward</th>
              <th>Required business</th>
              <th>Leg split <span className="ps-muted">(top / other)</span></th>
              <th>Reward</th>
              <th>Status</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {ranks.length === 0 && (
              <tr><td colSpan={7} className="ps-empty">No rewards configured.</td></tr>
            )}
            {ranks.map((r) => (
              <tr key={r.rankId}>
                <td>{r.rankId}</td>
                <td className="ps-strong">{r.rank_name}</td>
                <td>{Number(r.required_business).toLocaleString("en-IN")}</td>
                <td>{r.strong_leg_ratio}% / {r.other_legs_ratio}%</td>
                <td>
                  <div className="ps-strong">{inr(r.reward_amount)}</div>
                  {r.reward_description && <div className="ps-muted ps-small">{r.reward_description}</div>}
                </td>
                <td>
                  <Badge bg={r.reward_status === 1 ? "success" : "secondary"} className="ps-badge">
                    {r.reward_status === 1 ? "Active" : "Off"}
                  </Badge>
                </td>
                <td className="text-end">
                  <Button size="sm" variant="outline-primary" onClick={() => setEditing(r)}>Edit</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <RankModal show={!!editing} rank={editing} saving={saving} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
};

const RoyaltyModal = ({ show, rank, onClose, onSave, saving }) => {
  const [form, setForm] = useState(rank || {});
  useEffect(() => { if (show) setForm(rank || {}); }, [show, rank]);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    // save-rank takes the whole rank; only the royalty part changes here
    onSave({
      rankId: form.rankId,
      rank_name: form.rank_name,
      required_business: Number(form.required_business),
      reward_amount: Number(form.reward_amount),
      reward_description: form.reward_description || "",
      reward_status: Number(form.reward_status),
      strong_leg_ratio: Number(form.strong_leg_ratio),
      second_strong_leg_ratio: 0,
      other_legs_ratio: Number(form.other_legs_ratio),
      royalty_percent: Number(form.royalty_percent),
      royalty_duration: Math.round(Number(form.royalty_duration) || 0),
      royalty_status: Number(form.royalty_status),
    });
  };

  return (
    <Modal show={show} onHide={onClose} centered className="plan-settings-modal">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>Royalty · {form.rank_name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ps-form-grid">
            <Form.Group>
              <Form.Label>Royalty</Form.Label>
              <InputGroup>
                <Form.Control type="number" min="0" max="100" step="any" value={form.royalty_percent ?? ""} onChange={set("royalty_percent")} required />
                <InputGroup.Text>%</InputGroup.Text>
              </InputGroup>
              <Form.Text>Of the team's new package business each month.</Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>Paid for</Form.Label>
              <InputGroup>
                <Form.Control type="number" min="0" max="600" step="1" value={form.royalty_duration ?? ""} onChange={set("royalty_duration")} required />
                <InputGroup.Text>months</InputGroup.Text>
              </InputGroup>
              <Form.Text>0 = no end.</Form.Text>
            </Form.Group>
            <Form.Group className="ps-span-2">
              <Form.Check
                type="switch"
                id="rank-royalty-status"
                label={Number(form.royalty_status) === 1 ? "Royalty active for this reward level" : "Royalty off for this reward level"}
                checked={Number(form.royalty_status) === 1}
                onChange={(e) => setForm((f) => ({ ...f, royalty_status: e.target.checked ? 1 : 0 }))}
              />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? <Spinner size="sm" /> : "Save Royalty"}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const RoyaltyTab = ({ planId, ranks, onSaved }) => {
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async (payload) => {
    setSaving(true);
    const { data, error } = await apiCall("POST", "/plan-settings/save-rank", { planId, ...payload });
    setSaving(false);
    if (error) return toast.error(error.message || "Could not save the royalty.");
    toast.success(data?.message || "Royalty saved.");
    setEditing(null);
    onSaved();
  };

  return (
    <div className="ps-panel">
      <div className="ps-panel-head">
        <div>
          <h3>Royalty</h3>
          <p>Monthly royalty for each reward level: a share of the team's new package business, for a number of months.</p>
        </div>
      </div>
      <div className="ps-table-wrap">
        <Table hover className="ps-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Reward</th>
              <th>Royalty</th>
              <th>Paid for</th>
              <th>Status</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {ranks.length === 0 && (
              <tr><td colSpan={6} className="ps-empty">No rewards configured.</td></tr>
            )}
            {ranks.map((r) => (
              <tr key={r.rankId}>
                <td>{r.rankId}</td>
                <td className="ps-strong">{r.rank_name}</td>
                <td className="ps-strong">{Number(r.royalty_percent) || 0}%</td>
                <td>{Number(r.royalty_duration) > 0 ? `${r.royalty_duration} months` : "No end"}</td>
                <td>
                  <Badge bg={r.royalty_status === 1 ? "success" : "secondary"} className="ps-badge">
                    {r.royalty_status === 1 ? "Active" : "Off"}
                  </Badge>
                </td>
                <td className="text-end">
                  <Button size="sm" variant="outline-primary" onClick={() => setEditing(r)}>Edit</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <RoyaltyModal show={!!editing} rank={editing} saving={saving} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
};

const AdvisorRankModal = ({ show, rank, ranks, onClose, onSave, saving }) => {
  const [form, setForm] = useState(rank || {});
  useEffect(() => { if (show) setForm(rank || {}); }, [show, rank]);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const lower = (ranks || []).filter((r) => r.rankId < Number(form.rankId));

  const submit = (e) => {
    e.preventDefault();
    onSave({
      rankId: form.rankId,
      name: String(form.name || "").trim(),
      direct_business_required: Number(form.direct_business_required) || 0,
      team_count: Math.round(Number(form.team_count) || 0),
      team_rankId: Number(form.team_rankId) || 0,
      direct_income_percent: Number(form.direct_income_percent) || 0,
      recurring_percent: Number(form.recurring_percent) || 0,
      status: Number(form.status) === 0 ? 0 : 1,
    });
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg" className="plan-settings-modal">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Rank {form.rankId}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ps-form-grid">
            <Form.Group className="ps-span-2">
              <Form.Label>Rank name</Form.Label>
              <Form.Control value={form.name || ""} onChange={set("name")} required maxLength={60} />
            </Form.Group>

            {Number(form.rankId) === 1 ? (
              <div className="ps-span-2 ps-note ps-note-info">Reached as soon as the member's ID is active (any package).</div>
            ) : (
              <>
                <Form.Group>
                  <Form.Label>Direct business (BP)</Form.Label>
                  <Form.Control type="number" min="0" step="any" value={form.direct_business_required ?? ""} onChange={set("direct_business_required")} />
                  <Form.Text>Business points of direct referrals' packages. 0 = not used.</Form.Text>
                </Form.Group>
                <Form.Group>
                  <Form.Label>— OR — members in the team</Form.Label>
                  <InputGroup>
                    <Form.Control type="number" min="0" step="1" value={form.team_count ?? ""} onChange={set("team_count")} style={{ maxWidth: 90 }} />
                    <Form.Select value={form.team_rankId || ""} onChange={set("team_rankId")} disabled={!(Number(form.team_count) > 0)}>
                      <option value="">at rank…</option>
                      {lower.map((r) => (
                        <option key={r.rankId} value={r.rankId}>{r.name} or higher</option>
                      ))}
                    </Form.Select>
                  </InputGroup>
                  <Form.Text>Anywhere in the team. 0 = not used.</Form.Text>
                </Form.Group>
              </>
            )}

            <div className="ps-span-2"><hr className="my-1" /><Form.Label className="mb-0">Incomes at this rank</Form.Label></div>
            <Form.Group>
              <Form.Label>One Time (Direct Income)</Form.Label>
              <InputGroup>
                <Form.Control type="number" min="0" max="100" step="any" value={form.direct_income_percent ?? ""} onChange={set("direct_income_percent")} />
                <InputGroup.Text>%</InputGroup.Text>
              </InputGroup>
              <Form.Text>Of team packages (in BP), as a difference over lower ranks.</Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>Recurring (Career Recurring Bonus)</Form.Label>
              <InputGroup>
                <Form.Control type="number" min="0" max="100" step="any" value={form.recurring_percent ?? ""} onChange={set("recurring_percent")} />
                <InputGroup.Text>%</InputGroup.Text>
              </InputGroup>
              <Form.Text>Of team growth payouts, as a difference over lower ranks.</Form.Text>
            </Form.Group>
            <Form.Group className="ps-span-2">
              <Form.Check
                type="switch"
                id="advisor-rank-status"
                label={Number(form.status) === 0 ? "Rank off" : "Rank active"}
                checked={Number(form.status) !== 0}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.checked ? 1 : 0 }))}
              />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? <Spinner size="sm" /> : "Save Rank"}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const AdvisorRanksTab = ({ ranks, settings, onSaved }) => {
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rules, setRules] = useState(settings || {});
  const [savingRules, setSavingRules] = useState(false);
  useEffect(() => { setRules(settings || {}); }, [settings]);

  const saveRules = async (e) => {
    e.preventDefault();
    setSavingRules(true);
    const { data, error } = await apiCall("POST", "/plan-settings/save-advisor-settings", {
      real_estate_bp_ratio: Number(rules.real_estate_bp_ratio),
      fix_bp_ratio: Number(rules.fix_bp_ratio),
      override_percent: Number(rules.override_percent),
    });
    setSavingRules(false);
    if (error) return toast.error(error.message || "Could not save.");
    toast.success(data?.message || "Saved.");
    onSaved();
  };
  const perBp = (ratio) => (Number(ratio) > 0 ? `₹${Math.round((1 / Number(ratio)) * 100) / 100} = 1 BP` : "");
  const [recalculating, setRecalculating] = useState(false);
  const nameOf = (id) => ranks.find((r) => r.rankId === Number(id))?.name || `Rank ${id}`;

  const save = async (payload) => {
    setSaving(true);
    const { data, error } = await apiCall("POST", "/plan-settings/save-advisor-rank", payload);
    setSaving(false);
    if (error) return toast.error(error.message || "Could not save the rank.");
    toast.success(data?.message || "Rank saved.");
    setEditing(null);
    onSaved();
  };

  const recalculate = async () => {
    setRecalculating(true);
    const { data, error } = await apiCall("POST", "/advisor-ranks/recalculate");
    setRecalculating(false);
    if (error) return toast.error(error.message || "Could not recalculate.");
    toast.success(data?.message || "Ranks updated.");
  };

  const condition = (r) => {
    if (r.rankId === 1) return "Any activated package";
    const parts = [];
    if (r.direct_business_required > 0) parts.push(`Direct ${Number(r.direct_business_required).toLocaleString("en-IN")} BP`);
    if (r.team_count > 0) parts.push(`${r.team_count} × ${nameOf(r.team_rankId)} in team`);
    return parts.join(" or ") || "—";
  };
  const pctText = (v) => (Number(v) > 0 ? `${Number(v)}%` : "—");

  return (
    <div className="ps-panel">
      <div className="ps-panel-head">
        <div>
          <h3>Ranks</h3>
          <p>Advisor ranks, their conditions and what each rank earns. One Time and Recurring are paid as a difference up the sponsor chain; ranks never go down.</p>
        </div>
        <div className="ps-head-controls">
          <Button variant="outline-primary" size="sm" onClick={recalculate} disabled={recalculating}>
            {recalculating ? <Spinner size="sm" /> : "Recalculate ranks"}
          </Button>
        </div>
      </div>
      <div className="ps-note ps-note-info">
        <strong>50 : 50 rule.</strong> Direct business counts only the members' direct referrals' own packages, and the
        top direct counts for at most half the target. Team members of a rank are counted from the whole team, with at
        most half (rounded down) from the top leg and the rest from the other legs. Either condition reaches the rank.
      </div>
      <Form className="ps-rank-rules" onSubmit={saveRules}>
        <Form.Group>
          <Form.Label>Real Estate: BP per ₹</Form.Label>
          <Form.Control size="sm" type="number" min="0.01" max="10" step="any" value={rules.real_estate_bp_ratio ?? ""} onChange={(e) => setRules((r) => ({ ...r, real_estate_bp_ratio: e.target.value }))} />
          <Form.Text>{perBp(rules.real_estate_bp_ratio)}</Form.Text>
        </Form.Group>
        <Form.Group>
          <Form.Label>Fix plan: BP per ₹</Form.Label>
          <Form.Control size="sm" type="number" min="0.01" max="10" step="any" value={rules.fix_bp_ratio ?? ""} onChange={(e) => setRules((r) => ({ ...r, fix_bp_ratio: e.target.value }))} />
          <Form.Text>{perBp(rules.fix_bp_ratio)}</Form.Text>
        </Form.Group>
        <Form.Group>
          <Form.Label>Over Ride Bonus</Form.Label>
          <InputGroup size="sm">
            <Form.Control type="number" min="0" max="100" step="any" value={rules.override_percent ?? ""} onChange={(e) => setRules((r) => ({ ...r, override_percent: e.target.value }))} />
            <InputGroup.Text>%</InputGroup.Text>
          </InputGroup>
          <Form.Text>Sponsor's share of income earned by a same-or-higher rank member.</Form.Text>
        </Form.Group>
        <div className="ps-rank-rules-save">
          <Button type="submit" size="sm" disabled={savingRules}>{savingRules ? <Spinner size="sm" /> : "Save"}</Button>
        </div>
      </Form>
      <div className="ps-table-wrap">
        <Table hover className="ps-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Rank</th>
              <th>Condition</th>
              <th>One time</th>
              <th>Recurring</th>
              <th>Status</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {ranks.length === 0 && (
              <tr><td colSpan={7} className="ps-empty">No ranks configured.</td></tr>
            )}
            {ranks.map((r) => (
              <tr key={r.rankId}>
                <td>{r.rankId}</td>
                <td className="ps-strong">{r.name}</td>
                <td className="ps-small">{condition(r)}</td>
                <td className="ps-strong">{pctText(r.direct_income_percent)}</td>
                <td className="ps-strong">{pctText(r.recurring_percent)}</td>
                <td>
                  <Badge bg={r.status === 0 ? "secondary" : "success"} className="ps-badge">{r.status === 0 ? "Off" : "Active"}</Badge>
                </td>
                <td className="text-end">
                  <Button size="sm" variant="outline-primary" onClick={() => setEditing(r)}>Edit</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <AdvisorRankModal show={!!editing} rank={editing} ranks={ranks} saving={saving} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const PlanSettings = () => {
  const [planId, setPlanId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [tab, setTab] = useState("packages");

  const load = useCallback(async (id) => {
    const { data, error } = await apiCall("GET", `/plan-settings${id ? `?planId=${id}` : ""}`);
    if (error) {
      setLoadError(error.message || "Could not load plan settings.");
    } else {
      setLoadError("");
      setSettings(data?.data);
      setPlanId(data?.data?.planId);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const reload = () => load(planId);

  const activeCount = settings?.packages?.filter((p) => p.status === 1).length || 0;

  return (
    <div className="plan-settings">
      <div className="ps-header">
        <div>
          <h2>Plan Settings</h2>
          <p>Packages, level income and ranks of the plan. Changes are live as soon as they are saved.</p>
        </div>
        {settings?.planIds?.length > 1 && (
          <Form.Select
            value={planId || ""}
            onChange={(e) => { setLoading(true); load(e.target.value); }}
            className="ps-plan-select"
          >
            {settings.planIds.map((id) => (
              <option key={id} value={id}>Plan {id}</option>
            ))}
          </Form.Select>
        )}
      </div>

      {loading && (
        <div className="ps-loading"><Spinner /> Loading plan…</div>
      )}

      {!loading && loadError && (
        <div className="ps-note ps-note-warn">
          {loadError} <Button size="sm" variant="link" onClick={() => { setLoading(true); load(planId); }}>Retry</Button>
        </div>
      )}

      {!loading && settings && (
        <>
          <div className="ps-stats">
            <div className="ps-stat">
              <span>Active packages</span>
              <strong>{activeCount} <small>/ {settings.packages.length}</small></strong>
            </div>
            <div className="ps-stat">
              <span>Rewards with royalty</span>
              <strong>{settings.ranks.filter((r) => r.royalty_status === 1).length} <small>/ {settings.ranks.length}</small></strong>
            </div>
            <div className="ps-stat">
              <span>Rewards</span>
              <strong>{settings.ranks.length}</strong>
            </div>
          </div>

          <Tabs activeKey={tab} onSelect={(k) => setTab(k)} className="ps-tabs" mountOnEnter>
            <Tab eventKey="packages" title="Packages">
              <PackagesTab planId={planId} packages={settings.packages} onSaved={reload} />
            </Tab>
            <Tab eventKey="advisor-ranks" title="Ranks">
              <AdvisorRanksTab ranks={settings.advisor_ranks || []} settings={settings.advisor_settings} onSaved={reload} />
            </Tab>
            <Tab eventKey="ranks" title="Rewards">
              <RanksTab planId={planId} ranks={settings.ranks} onSaved={reload} />
            </Tab>
            <Tab eventKey="royalty" title="Royalty">
              <RoyaltyTab planId={planId} ranks={settings.ranks} onSaved={reload} />
            </Tab>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default PlanSettings;
